import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ACTION_OPTIONS = [
  "",
  "workspace.plan_changed",
  "workspace.markup_changed",
  "workspace.markup_reset",
  "workspace.trial_adjusted",
  "workspace.balance_adjusted",
  "workspace.suspended",
  "workspace.resumed",
  "workspace.suspended_reason_changed",
  "plan_config.updated",
  "user.superadmin_changed",
];

export default function SuperadminAudit() {
  const [filter, setFilter] = useState({ action: "", actor: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filter.action) params.action = filter.action;
      if (filter.actor.trim()) params.actor = filter.actor.trim();
      const { data: resp } = await api.get("/admin/audit", { params });
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter.action, filter.actor, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="superadmin-audit">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-[#2C2A29]">
            Audit log
          </h2>
          <p className="font-sans text-sm text-[#7A7571] mt-1">
            Every administrative change is recorded here. {data.total} entries.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <select
          data-testid="audit-action-filter"
          value={filter.action}
          onChange={(e) => {
            setPage(1);
            setFilter((f) => ({ ...f, action: e.target.value }));
          }}
          className="px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
        >
          {ACTION_OPTIONS.map((a) => (
            <option key={a || "any"} value={a}>
              {a || "Any action"}
            </option>
          ))}
        </select>
        <input
          data-testid="audit-actor-filter"
          type="text"
          placeholder="Filter by actor email…"
          value={filter.actor}
          onChange={(e) => {
            setPage(1);
            setFilter((f) => ({ ...f, actor: e.target.value }));
          }}
          className="px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm flex-1 min-w-[240px] max-w-md focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
        />
      </div>

      <div className="rounded-xl border border-[#DCD7CE] bg-white overflow-hidden">
        {loading ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-audit-loading"
          >
            Loading audit log…
          </div>
        ) : data.items.length === 0 ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-audit-empty"
          >
            No audit entries match these filters.
          </div>
        ) : (
          data.items.map((a) => (
            <div
              key={a.id}
              data-testid={`audit-row-${a.id}`}
              className="px-4 py-3 border-b border-[#EFEAE0] last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="px-1.5 py-0.5 rounded bg-[#EFEAE0] font-mono text-xs text-[#2C2A29]">
                      {a.action}
                    </code>
                    <span className="font-sans text-xs text-[#7A7571]">
                      by {a.actor_email || "system"}
                    </span>
                  </div>
                  {a.target_workspace_id ? (
                    <p className="font-mono text-[11px] text-[#A5A29E]">
                      ws: {a.target_workspace_id}
                    </p>
                  ) : null}
                  {a.target_user_id ? (
                    <p className="font-mono text-[11px] text-[#A5A29E]">
                      user: {a.target_user_id}
                    </p>
                  ) : null}
                  {a.payload?.reason ? (
                    <p className="font-sans text-xs italic text-[#7A7571] mt-1">
                      “{a.payload.reason}”
                    </p>
                  ) : null}
                  {a.payload?.before !== undefined ||
                  a.payload?.after !== undefined ? (
                    <p className="font-mono text-[11px] text-[#3a3735] mt-1">
                      {JSON.stringify(a.payload.before)} →{" "}
                      {JSON.stringify(a.payload.after)}
                    </p>
                  ) : null}
                </div>
                <p className="font-sans text-xs text-[#7A7571] whitespace-nowrap">
                  {a.created_at
                    ? new Date(a.created_at).toLocaleString()
                    : ""}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {data.total > 50 ? (
        <div className="flex items-center justify-between mt-4">
          <p className="font-sans text-xs text-[#7A7571]">
            Page {page} &middot; {data.items.length} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="superadmin-audit-prev"
              className="px-3 py-1.5 rounded-md font-sans text-xs border border-[#DCD7CE] bg-white disabled:opacity-40 hover:bg-[#EFEAE0] transition-colors"
            >
              Previous
            </button>
            <button
              disabled={!data.has_more}
              onClick={() => setPage((p) => p + 1)}
              data-testid="superadmin-audit-next"
              className="px-3 py-1.5 rounded-md font-sans text-xs border border-[#DCD7CE] bg-white disabled:opacity-40 hover:bg-[#EFEAE0] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
